import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Box,
  CircularProgress,
  Alert,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getStripePromise, reloadStripePromise } from '../../utils/stripeLoader';

/**
 * Modal pour gérer l'authentification 3DS lors de la résolution d'un litige
 * avec capture off-session d'un deposit
 */
const DisputeAuthenticationModal = ({ 
  open, 
  onClose, 
  clientSecret,
  paymentMethodId, // Nécessaire pour confirmer un PI en requires_payment_method
  paymentIntentId, // Pour appeler cancel API
  disputeId, // Pour appeler cancel API
  onSuccess, 
  onError,
  onCancel // Callback pour notifier l'abandon
}) => {
  const { t } = useTranslation('disputes');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [hasProcessed, setHasProcessed] = useState(false); // Guard contre double exécution

  // Reset state when modal opens/closes or clientSecret changes
  useEffect(() => {
    if (!open) {
      setProcessing(false);
      setError(null);
      setHasProcessed(false);
    }
  }, [open]);

  // Reset hasProcessed when clientSecret changes
  useEffect(() => {
    setHasProcessed(false);
  }, [clientSecret]);

  useEffect(() => {
    const handleAuthentication = async () => {
      if (!open || !clientSecret || processing || hasProcessed) return;

      setProcessing(true);
      setHasProcessed(true); // Marquer comme traité IMMÉDIATEMENT
      setError(null);

      try {
        let stripe = await getStripePromise();
        if (!stripe) {
          stripe = await reloadStripePromise();
        }
        
        if (!stripe) {
          throw new Error(t('authentication.stripeLoadFailed'));
        }

        // Inspecter d'abord le PaymentIntent pour décider comment confirmer.
        // - requires_action / requires_confirmation: confirmCardPayment(clientSecret) suffit (PI a déjà un PM attaché)
        // - requires_payment_method: il faut un nouveau payment_method (si on n'a pas de UI pour collecter une carte,
        //   on ne peut pas le résoudre automatiquement)
        let retrieved = null;
        try {
          retrieved = await stripe.retrievePaymentIntent(clientSecret);
        } catch (e) {
          // best-effort: si retrieve échoue on tente confirm simple
        }

        const initialPi = retrieved?.paymentIntent;
        const initialStatus = initialPi?.status;

        if (initialStatus === 'requires_payment_method' && !paymentMethodId) {
          const err = new Error(t('authentication.paymentMethodRequired'));
          err.code = 'requires_payment_method';
          throw err;
        }

        const confirmArgs = (initialStatus === 'requires_payment_method' && paymentMethodId)
          ? [clientSecret, { payment_method: paymentMethodId }]
          : [clientSecret];

        console.log('[3DS] Confirming payment', {
          payment_intent_status: initialStatus,
          has_payment_method_id: !!paymentMethodId
        });

        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(...confirmArgs);

        if (stripeError) {
          console.error('[3DS] Payment confirmation failed:', stripeError);
          setError(stripeError.message || t('authentication.paymentFailed'));
          setHasProcessed(false); // Permettre un retry
          if (onError) {
            onError(stripeError);
          }
          return;
        }

        if (paymentIntent && paymentIntent.status === 'succeeded') {
          console.log('[3DS] Payment succeeded:', paymentIntent.id);
          if (onSuccess) {
            onSuccess(paymentIntent);
          }
        } else {
          const errorMsg = t('authentication.unexpectedStatus', { status: paymentIntent?.status });
          console.error('[3DS]', errorMsg);
          setError(errorMsg);
          setHasProcessed(false); // Permettre un retry
          if (onError) {
            onError(new Error(errorMsg));
          }
        }
      } catch (err) {
        console.error('[3DS] Error during payment confirmation:', err);
        const fallbackMsg = err?.message || t('authentication.error');
        const needsNewPm =
          err?.code === 'payment_intent_authentication_failure' ||
          err?.code === 'requires_payment_method' ||
          (fallbackMsg && fallbackMsg.toLowerCase().includes('requires_payment_method'));

        setError(
          needsNewPm
            ? t('authentication.paymentRejected')
            : fallbackMsg
        );
        setHasProcessed(false); // Permettre un retry
        if (onError) {
          onError(err);
        }
      } finally {
        setProcessing(false);
      }
    };

    handleAuthentication();
  }, [open, clientSecret, paymentMethodId]); // ✅ CORRECTION: Retirer processing, hasProcessed, onSuccess, onError des dépendances

  // Handler pour la fermeture du modal (abandon/cancel)
  const handleClose = () => {
    if (onCancel && paymentIntentId && disputeId) {
      console.log('[3DS] User closed modal - calling cancel API', { paymentIntentId, disputeId });
      onCancel(paymentIntentId, disputeId, 'user_canceled');
    }
    if (onClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={false}
    >
      <DialogTitle>
        {t('authentication.title', 'Authentification requise')}
      </DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {t('authentication.error', 'Erreur d\'authentification')}: {error}
            </Typography>
          </Alert>
        ) : (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              {t('authentication.message', 'Votre banque requiert une authentification supplémentaire pour valider cette transaction. Veuillez suivre les instructions qui s\'affichent.')}
            </DialogContentText>
            
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
              <CircularProgress />
            </Box>
            
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
              {t('authentication.processing', 'Authentification en cours...')}
            </Typography>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DisputeAuthenticationModal;
